<template>
    <Modal :open="open" size="4xl" @close="handleClose">
        <h3
            class="mb-3 border-b border-slate-400 pb-2 text-lg font-bold leading-6 text-white"
        >
            {{ editingPlan ? "Editar Plano" : "Novo Plano" }}
        </h3>

        <div
            v-if="editingPlan"
            class="mb-4 rounded bg-yellow-100 p-3 text-yellow-800"
        >
            <strong>Atenção:</strong> Ao editar um plano, todos os produtores
            vinculados a este plano serão atualizados.
        </div>

        <form @submit.prevent="handleSubmit">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div class="mb-2">
                    <label
                        for="name"
                        class="mb-2 block text-sm font-medium text-white"
                        >Nome</label
                    >
                    <input
                        v-model="form.name"
                        type="text"
                        name="name"
                        id="name"
                        class="text-input"
                        required
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="fixedFee"
                        class="mb-2 block text-sm font-medium text-white"
                        >Cobrança Fixa (R$)</label
                    >
                    <money-input
                        v-model="form.fixedFee"
                        name="fixedFee"
                        id="fixedFee"
                        :options="{ precision: 2, min: 0 }"
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="fixedFeeInternational"
                        class="mb-2 block text-sm font-medium text-white"
                        >Cobrança Fixa Internacional (R$)</label
                    >
                    <money-input
                        v-model="form.fixedFeeInternational"
                        name="fixedFeeInternational"
                        id="fixedFeeInternational"
                        :options="{ precision: 2, min: 0 }"
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="receivementTime"
                        class="mb-2 block text-sm font-medium text-white"
                        >Prazo de Recebimento (dias)</label
                    >
                    <input
                        v-model.number="form.receivementTime"
                        type="number"
                        name="receivementTime"
                        id="receivementTime"
                        min="0"
                        class="text-input"
                        required
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="platformFee"
                        class="mb-2 block text-sm font-medium text-white"
                        >Taxa da Plataforma (%)</label
                    >
                    <input
                        v-model.number="form.platformFee"
                        type="number"
                        name="platformFee"
                        id="platformFee"
                        min="0"
                        step="0.01"
                        class="text-input"
                        required
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="anticipationFee"
                        class="mb-2 block text-sm font-medium text-white"
                        >Taxa de Antecipação (%)</label
                    >
                    <input
                        v-model.number="form.anticipationFee"
                        type="number"
                        name="anticipationFee"
                        id="anticipationFee"
                        min="0"
                        step="0.01"
                        class="text-input"
                        required
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="anticipationDays"
                        class="mb-2 block text-sm font-medium text-white"
                        >Dias para Antecipação</label
                    >
                    <input
                        v-model.number="form.anticipationDays"
                        type="number"
                        name="anticipationDays"
                        id="anticipationDays"
                        min="0"
                        class="text-input"
                    />
                </div>
                <div class="mb-2">
                    <label
                        for="anticipationLimit"
                        class="mb-2 block text-sm font-medium text-white"
                        >Limite de Antecipação (%)</label
                    >
                    <input
                        v-model.number="form.anticipationLimit"
                        type="number"
                        name="anticipationLimit"
                        id="anticipationLimit"
                        min="0"
                        max="100"
                        class="text-input"
                    />
                </div>
                <div class="mb-2">
                    <label class="flex items-center">
                        <input
                            v-model="form.active"
                            type="checkbox"
                            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span class="ml-2 text-sm font-medium text-white">
                            Plano Ativo
                        </span>
                    </label>
                </div>
                <!-- não terá planos em destaque, mas caso seja necessário, pode vir a ser usado. -->
                <!-- <div class="mb-2">
                    <label class="flex items-center">
                        <input
                            v-model="form.featured"
                            type="checkbox"
                            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span class="ml-2 text-sm font-medium text-white">
                            Plano em Destaque
                        </span>
                    </label>
                </div> -->
            </div>

            <!-- Configurações por método de pagamento -->
            <div class="mt-6 border-t border-slate-400 pt-4">
                <h4 class="text-md mb-3 font-semibold text-white">
                    Configurações por Método
                </h4>

                <!-- Cartão -->
                <div class="config-method-section">
                    <h5 class="config-method-title">Cartão</h5>
                    <div class="config-method-grid">
                        <div>
                            <label class="config-method-label"
                                >Prazo de Saque (dias)</label
                            >
                            <input
                                v-model.number="form.card.withdrawTime"
                                type="number"
                                min="0"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa da Plataforma (%)</label
                            >
                            <input
                                v-model.number="form.card.platformFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa de Parcelamento (%)</label
                            >
                            <input
                                v-model.number="form.card.installmentFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                    </div>
                </div>

                <!-- Boleto -->
                <div class="config-method-section">
                    <h5 class="config-method-title">Boleto</h5>
                    <div class="config-method-grid">
                        <div>
                            <label class="config-method-label"
                                >Prazo de Saque (dias)</label
                            >
                            <input
                                v-model.number="form.bank_slip.withdrawTime"
                                type="number"
                                min="0"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa da Plataforma (%)</label
                            >
                            <input
                                v-model.number="form.bank_slip.platformFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa de Parcelamento (%)</label
                            >
                            <input
                                v-model.number="form.bank_slip.installmentFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                    </div>
                </div>

                <!-- PIX -->
                <div class="config-method-section">
                    <h5 class="config-method-title">PIX</h5>
                    <div class="config-method-grid">
                        <div>
                            <label class="config-method-label"
                                >Prazo de Saque (dias)</label
                            >
                            <input
                                v-model.number="form.pix.withdrawTime"
                                type="number"
                                min="0"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa da Plataforma (%)</label
                            >
                            <input
                                v-model.number="form.pix.platformFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa de Parcelamento (%)</label
                            >
                            <input
                                v-model.number="form.pix.installmentFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                    </div>
                </div>

                <!-- PIX Recorrente -->
                <div class="config-method-section">
                    <h5 class="config-method-title">PIX Recorrente</h5>
                    <div class="config-method-grid-2">
                        <div>
                            <label class="config-method-label"
                                >Prazo de Saque (dias)</label
                            >
                            <input
                                v-model.number="form.pix_recurring.withdrawTime"
                                type="number"
                                min="0"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa da Plataforma (%)</label
                            >
                            <input
                                v-model.number="form.pix_recurring.platformFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                    </div>
                </div>

                <!-- Internacional -->
                <div class="config-method-section">
                    <h5 class="config-method-title">Internacional</h5>
                    <div class="config-method-grid-2">
                        <div>
                            <label class="config-method-label"
                                >Prazo de Saque (dias)</label
                            >
                            <input
                                v-model.number="form.international.withdrawTime"
                                type="number"
                                min="0"
                                class="config-method-input"
                            />
                        </div>
                        <div>
                            <label class="config-method-label"
                                >Taxa da Plataforma (%)</label
                            >
                            <input
                                v-model.number="form.international.platformFee"
                                type="number"
                                min="0"
                                step="0.01"
                                class="config-method-input"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                    :disabled="loading"
                    type="submit"
                    class="inline-flex w-full justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                >
                    Salvar
                </button>
                <button
                    :disabled="loading"
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                    @click="handleClose"
                >
                    Cancelar
                </button>
            </div>
        </form>
    </Modal>
</template>

<script>
import Modal from "../layouts/Modal.vue";
import MoneyInput from "../forms/MoneyInput.vue";

export default {
    name: "PlanModal",
    components: {
        Modal,
        MoneyInput,
    },
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        editingPlan: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: {
                name: "",
                fixedFee: 0,
                fixedFeeInternational: 0,
                receivementTime: 0,
                platformFee: 0,
                anticipationFee: 0,
                anticipationDays: null,
                anticipationLimit: null,
                platformFees: null,
                installmentFees: null,
                transferDays: null,
                active: true,
                featured: false,
                card: {
                    withdrawTime: 0,
                    platformFee: 0,
                    installmentFee: 0,
                },
                bank_slip: {
                    withdrawTime: 0,
                    platformFee: 0,
                    installmentFee: 0,
                },
                pix: { withdrawTime: 0, platformFee: 0, installmentFee: 0 },
                pix_recurring: { withdrawTime: 0, platformFee: 0 },
                international: { withdrawTime: 0, platformFee: 0 },
            },
        };
    },
    watch: {
        editingPlan: {
            handler(newPlan) {
                if (newPlan) {
                    // Mapeia os dados da API para o formato do formulário
                    this.form = {
                        name: newPlan.name || "",
                        fixedFee: parseFloat((newPlan.fixed_fee || 0) / 100),
                        fixedFeeInternational: parseFloat(
                            (newPlan.fixed_fee_international || 0) / 100,
                        ),
                        receivementTime: parseInt(
                            newPlan.receivement_time || 0,
                        ),
                        platformFee: parseFloat(newPlan.platform_fee || 0),
                        anticipationFee: parseFloat(
                            newPlan.anticipation_fee || 0,
                        ),
                anticipationDays: newPlan.anticipation_days
                    ? parseInt(newPlan.anticipation_days)
                    : null,
                anticipationLimit: newPlan.anticipation_limit
                    ? parseInt(newPlan.anticipation_limit)
                    : null,
                active:
                    newPlan.active !== undefined
                        ? newPlan.active
                        : true,
                featured:
                    newPlan.featured !== undefined
                        ? newPlan.featured
                        : false,
                platformFees: newPlan.platform_fees || null,
                installmentFees: newPlan.installment_fees || null,
                transferDays: newPlan.transfer_days || null,
                card: {
                    withdrawTime: newPlan.card?.withdraw_time || 0,
                            platformFee: newPlan.card?.platform_fee || 0,
                            installmentFee: newPlan.card?.installment_fee || 0,
                        },
                        bank_slip: {
                            withdrawTime: newPlan.bank_slip?.withdraw_time || 0,
                            platformFee: newPlan.bank_slip?.platform_fee || 0,
                            installmentFee:
                                newPlan.bank_slip?.installment_fee || 0,
                        },
                        pix: {
                            withdrawTime: newPlan.pix?.withdraw_time || 0,
                            platformFee: newPlan.pix?.platform_fee || 0,
                            installmentFee: newPlan.pix?.installment_fee || 0,
                        },
                        pix_recurring: {
                            withdrawTime: newPlan.pix_recurring?.withdraw_time || 0,
                            platformFee: newPlan.pix_recurring?.platform_fee || 0,
                        },
                        international: {
                            withdrawTime:
                                newPlan.international?.withdraw_time || 0,
                            platformFee:
                                newPlan.international?.platform_fee || 0,
                        },
                    };
                } else {
                    this.resetForm();
                }
            },
            immediate: true,
        },
    },
    methods: {
        resetForm() {
            this.form = {
                name: "",
                fixedFee: 0,
                fixedFeeInternational: 0,
                receivementTime: 0,
                platformFee: 0,
                anticipationFee: 0,
                anticipationDays: null,
                anticipationLimit: null,
                active: true,
                featured: false,
                card: {
                    withdrawTime: 0,
                    platformFee: 0,
                    installmentFee: 0,
                },
                bank_slip: {
                    withdrawTime: 0,
                    platformFee: 0,
                    installmentFee: 0,
                },
                pix: { withdrawTime: 0, platformFee: 0, installmentFee: 0 },
                pix_recurring: { withdrawTime: 0, platformFee: 0 },
                international: { withdrawTime: 0, platformFee: 0 },
            };
        },
        handleSubmit() {
            // Converter camelCase para snake_case antes de enviar
            const formData = {
                name: this.form.name,
                active: this.form.active,
                featured: this.form.featured,
                fixedFee: this.form.fixedFee,
                fixedFeeInternational: this.form.fixedFeeInternational,
                receivementTime: this.form.receivementTime,
                platformFee: this.form.platformFee,
                anticipationFee: this.form.anticipationFee,
                anticipationDays: this.form.anticipationDays,
                anticipationLimit: this.form.anticipationLimit,
                platformFees: this.form.platformFees,
                installmentFees: this.form.installmentFees,
                transferDays: this.form.transferDays,
                bank_slip: {
                    withdraw_time: this.form.bank_slip.withdrawTime,
                    platform_fee: this.form.bank_slip.platformFee,
                    installment_fee: this.form.bank_slip.installmentFee,
                },
                card: {
                    withdraw_time: this.form.card.withdrawTime,
                    platform_fee: this.form.card.platformFee,
                    installment_fee: this.form.card.installmentFee,
                },
                pix: {
                    withdraw_time: this.form.pix.withdrawTime,
                    platform_fee: this.form.pix.platformFee,
                    installment_fee: this.form.pix.installmentFee,
                },
                pix_recurring: {
                    withdraw_time: this.form.pix_recurring.withdrawTime,
                    platform_fee: this.form.pix_recurring.platformFee,
                },
                international: {
                    withdraw_time: this.form.international.withdrawTime,
                    platform_fee: this.form.international.platformFee,
                },
            };
            
            this.$emit("save", formData);
        },
        handleClose() {
            this.$emit("close");
        },
    },
};
</script>

<style scoped>
@reference "../../../css/app.css";

/* Estilos para os checkboxes */
input[type="checkbox"] {
    @apply h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500;
}

/* Garantir que os labels dos checkboxes tenham espaçamento adequado */
.flex.items-center {
    @apply mb-2;
}

/* Estilos para os campos de configuração por método */
.config-method-section {
    @apply mb-4 rounded border border-slate-400 p-3;
}

.config-method-title {
    @apply mb-2 text-sm font-medium text-white;
}

.config-method-grid {
    @apply grid grid-cols-1 gap-3 md:grid-cols-3;
}

.config-method-grid-2 {
    @apply grid grid-cols-1 gap-3 md:grid-cols-2;
}

.config-method-label {
    @apply mb-1 block text-xs text-slate-300;
}

.config-method-input {
    @apply block w-full rounded-lg border border-2 border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-indigo-500 dark:focus:ring-1 dark:focus:ring-indigo-500/50;
}
</style>
